import React, { useEffect, useState } from 'react';
import { GetJson, PutJson, PostJson } from '@awp/helpers/fetchReact';
import { REST_API, FORM_MODE } from '@awp/constants';
import { Row, Col, Card, Button, Spinner, Container } from 'react-bootstrap';
import {
  FormTextReadOnly,
  FormEmailReadOnly,
  FormRadioGroup,
  FormRadio,
  FormSelect,
  FormTextArea,
  FormInput,
} from '@awp/components/Common/AwpFormInputs';
import { Formik, Form } from 'formik';
import { selectRoles } from '@awp/stores/dropdownsSlice';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { getCrud } from '@awp/shared/Crud';

const UserEdit = ({ setMode, mode, userId, data, setData, setLoading, loading }) => {
  const [userData, setUserData] = useState({});
  const [errors, setErrors] = useState(null);

  const rolesDropdown = useSelector(selectRoles);
  const gatewayType = 'aps';

  const crud = getCrud(userData.UserType === gatewayType ? userData.UserName : userData.FullName, mode);
  const { readOnly } = crud;

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setUserData({ UserName: '', UserType: gatewayType, userType: gatewayType, IsActive: 'Y', Note: '' });
      return;
    }
    setLoading(true);
    const url = REST_API.USERS + userId;

    GetJson(url)
      .then((response) => response.json())
      .then((user) => {
        setUserData(user);
        setLoading(false);
      });
  }, []);

  const createGatewayUser = (setSubmitting, setErrors, gatewayUserDto) => {
    const url = REST_API.GATEWAY_USER;
    PostJson(url, gatewayUserDto)
      .then(async (response) => {
        if (response.ok) {
          const user = await response.json();
          setUserData(user);
          updateData(user);
          toast.success('Gateway User has been created');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const UpdateUser = (userDto, setErrors, setSubmitting, resetForm) => {
    const _user = { ...userDto };
    const url = REST_API.USERS + _user.Id;
    PutJson(url, _user)
      .then(async (response) => {
        if (response.ok) {
          toast.success('User profile has been updated');
          const user = await response.json();
          setUserData(user);
          updateData(user);
          let vals = {
            validateOnMount: true,
            isActive: user.IsActive,
            roles: user.Roles,
            notes: user.NoteComment,
          };
          if (user.UserType === gatewayType) {
            vals.organization = user.Organization;
            vals.emailAddress = user.EmailAddress;
            vals.userName = user.UserName;
          }
          resetForm({
            values: vals,
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    let userDto = {
      ...userData,
      IsActive: values.isActive,
      RoleNames: values.roles.map((role) => role.label).join(','),
      Roles: values.roles,
      NoteComment: values.notes,
    };
    if (userData.UserType === gatewayType) {
      userDto.UserName = values.userName;
      userDto.EmailAddress = values.emailAddress;
      userDto.Organization = values.organization;
    }
    if (userData.UserType === gatewayType && mode === FORM_MODE.CREATE) {
      createGatewayUser(setSubmitting, setErrors, userDto);
    } else if (mode === FORM_MODE.EDIT) {
      UpdateUser(userDto, setErrors, setSubmitting, resetForm);
    }
  };

  const updateData = (userDto) => {
    if (mode === FORM_MODE.EDIT) {
      const newData = [...data];
      const index = newData.findIndex((x) => x.Id === userDto.Id);
      if (index < 0) return;

      newData[index] = userDto;
      setData(newData);
    } else {
      setData([...data, userDto]);
    }
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={{
              validateOnMount: true,
              isActive: mode === FORM_MODE.CREATE ? 'Y' : userData.IsActive,
              roles: mode === FORM_MODE.CREATE ? [] : userData.Roles,
              notes: userData.NoteComment,
              emailAddress: userData.EmailAddress,
              organization: userData.Organization,
              userName: userData.UserName,
              userType: mode === FORM_MODE.CREATE ? gatewayType : userData.UserType,
            }}
            initialTouched={{
              userName: true,
              roles: true,
            }}
            validationSchema={Yup.object({
              roles: Yup.array().min(1, 'At least one role must be selected'),
              notes: Yup.string().max(1000, 'Maximum 1000 characters are allowed'),
              userName: Yup.string().test('userName', 'Client Id is required', (value, context) => {
                if (
                  !value &&
                  mode === FORM_MODE.CREATE &&
                  context &&
                  context.parent &&
                  context.parent.userType === gatewayType
                )
                  return false;
                return true;
              }),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <span className="awp-text-btn" onClick={() => setMode('list')}>
                  Registered Users
                </span>
                <span className="mx-3">&gt;</span>
                <span>
                  {userData.UserName === '' || userData.FullName === ''
                    ? 'Add Aps Client'
                    : userData.UserType === gatewayType
                    ? userData.UserName
                    : userData.FullName}
                </span>
                <Card border="light" className="page-card">
                  <Card.Header>
                    <Row className="mt-4"></Row>
                    {crud.getHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={4} className="mb-4">
                        {userData.UserType === gatewayType ? (
                          <FormInput label="Client ID (required)" readOnly={readOnly} name="userName" id="userName" />
                        ) : (
                          <FormTextReadOnly label="User ID" textValue={userData.UserName} />
                        )}
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormTextReadOnly label="User Type" textValue={userData.UserType} />
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormRadioGroup
                          readOnly={readOnly}
                          label="Current Status"
                          textValue={userData.IsActive === 'Y' ? 'Active' : 'Disabled'}
                          name="isActive"
                          id="isActive"
                        >
                          <Row>
                            <Col sm={5}>
                              <FormRadio id="Active" label="Active" name="isActive" value="Y" />
                            </Col>
                            <Col sm={5}>
                              <FormRadio id="Disabled" label="Disabled" name="isActive" value="N" />
                            </Col>
                          </Row>
                        </FormRadioGroup>
                      </Col>
                    </Row>
                    {userData.UserType !== gatewayType && (
                      <Row>
                        <Col sm={4} className="mb-4">
                          <FormTextReadOnly label="Last Login" textValue={userData.LastLoginPst} />
                        </Col>
                        <Col sm={4} className="mb-4">
                          <FormTextReadOnly label="Name" textValue={userData.FullName} />
                        </Col>
                        <Col sm={4} className="mb-4">
                          <FormTextReadOnly label="User Title" textValue={userData.Title} />
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col sm={4} className="mb-4">
                        {userData.UserType === gatewayType ? (
                          <FormInput label="Organization" readOnly={readOnly} name="organization" id="organization" />
                        ) : (
                          <FormTextReadOnly label="Organization" textValue={userData.Organization} />
                        )}
                      </Col>
                      <Col sm={4} className="mb-4">
                        {userData.UserType === gatewayType ? (
                          <FormInput label="Email" readOnly={readOnly} name="emailAddress" id="emailAddress" />
                        ) : (
                          <FormEmailReadOnly label="Email" textValue={userData.EmailAddress} />
                        )}
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormSelect
                          readOnly={readOnly}
                          label={userData.UserType === gatewayType ? 'User Roles (required)' : 'User Roles'}
                          id="roles"
                          name="roles"
                          textValue={userData.RoleNames}
                          options={rolesDropdown.options}
                          isMulti={true}
                        ></FormSelect>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={4} className="mb-4">
                        <FormTextReadOnly
                          label="Last Access Updated"
                          textValue={userData.LastAccessChangeTimestampPst}
                        />
                      </Col>
                      <Col sm={8} className="mb-4">
                        <FormTextArea readOnly={readOnly} label="Notes" id="notes" name="notes" rows={3} />
                      </Col>
                    </Row>
                  </Card.Body>
                  <Card.Footer>
                    <Row className="my-2">
                      {mode === FORM_MODE.EDIT || mode === FORM_MODE.CREATE ? (
                        <Col sm={12} md={6} lg={7}>
                          <div className="mr-3 mr-micro-0 mr-md-0" style={{ float: 'left' }}>
                            <Button
                              variant="outline-primary"
                              onClick={() => {
                                if (userData.UserType === gatewayType && mode === FORM_MODE.CREATE) {
                                  setMode(FORM_MODE.LIST);
                                } else {
                                  props.resetForm();
                                  setMode(FORM_MODE.VIEW);
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="primary"
                              className="ml-2"
                              disabled={!props.isValid || !props.dirty || props.isSubmitting}
                            >
                              {props.isSubmitting ? (
                                <>
                                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                  Saving...
                                </>
                              ) : (
                                <span>Save Changes</span>
                              )}
                            </Button>
                          </div>
                        </Col>
                      ) : (
                        <Col sm={12} md={6} className="pr-0">
                          <div className="mr-3 mr-mini-0">
                            <Button className="mr-2" variant="outline-primary" onClick={() => setMode('edit')}>
                              Edit Profile
                            </Button>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
        </>
      )}
    </>
  );
};

export default UserEdit;
